import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "../../../../assets/styles/ButtonStyle";
import { Sub2 } from "../../../../assets/styles/Typography";
import { palette } from "../../../../assets/styles/Palette";
import { updateEduTimetableOnServer } from "../../../../utils/indexedDB";

// API 대시보드용 콘텐츠 편성 팝업 (디자인은 OrganismContentList의 MoleculeContentPopup와 동일하게 구성)
const MoleculeApiContentPopup = ({ onClose, contentList = [], lotationDetail = null, onLotationUpdate = null }) => {
  const [availableContents, setAvailableContents] = useState([]);
  const [scheduledContents, setScheduledContents] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedScheduled, setSelectedScheduled] = useState([]);

  useEffect(() => {
    try {
      let currentScheduledContents = [];
      if (lotationDetail) {
        currentScheduledContents = lotationDetail.lotationContents || [];
      }
      const scheduledIds = new Set(currentScheduledContents.map(item => item._id));
      const available = contentList.filter(item => !scheduledIds.has(item._id));
      setAvailableContents(available);
      setScheduledContents(currentScheduledContents);
    } catch (e) {
      setAvailableContents(contentList);
      setScheduledContents([]);
    }
  }, [contentList, lotationDetail]);

  const moveToScheduled = () => {
    const itemsToMove = availableContents.filter((_, idx) => selectedAvailable.includes(idx));
    setScheduledContents(prev => [...prev, ...itemsToMove]);
    setAvailableContents(prev => prev.filter((_, idx) => !selectedAvailable.includes(idx)));
    setSelectedAvailable([]);
  };

  const moveToAvailable = () => {
    const itemsToMove = scheduledContents.filter((_, idx) => selectedScheduled.includes(idx));
    setAvailableContents(prev => [...prev, ...itemsToMove]);
    setScheduledContents(prev => prev.filter((_, idx) => !selectedScheduled.includes(idx)));
    setSelectedScheduled([]);
  };

  const handleConfirm = async () => {
    try {
      if (!lotationDetail || !lotationDetail.id) {
        console.error("Lotation detail not available");
        onClose();
        return;
      }
      const updateData = {
        lotationContents: [...scheduledContents],
      };
      await updateEduTimetableOnServer(lotationDetail.id, updateData);
      if (onLotationUpdate) {
        await onLotationUpdate();
      }
      onClose();
    } catch (error) {
      console.error("Failed to confirm content:", error);
    }
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <PopupContainer>
        <Header>
          <Heading>콘텐츠 편성</Heading>
          <CloseButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </CloseButton>
        </Header>

        <ContentSection>
          <ContentArea>
            <ContentColumn>
              <ColumnHeader>편성 가능한 콘텐츠</ColumnHeader>
              <ContentList>
                {availableContents.map((content, index) => (
                  <ContentItem
                    key={content._id}
                    selected={selectedAvailable.includes(index)}
                    onClick={() =>
                      setSelectedAvailable(prev =>
                        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
                      )
                    }
                  >
                    <CheckboxContainer>
                      {selectedAvailable.includes(index) ? <CheckedSvg /> : <UncheckedSvg />}
                    </CheckboxContainer>
                    <ContentTitle>{content.contentData?.title || content.dataAnalysis?.title}</ContentTitle>
                  </ContentItem>
                ))}
              </ContentList>
            </ContentColumn>

            <ArrowColumn>
              <ArrowButton onClick={moveToScheduled} disabled={selectedAvailable.length === 0}>
                <RightArrowSvg />
              </ArrowButton>
              <ArrowButton onClick={moveToAvailable} disabled={selectedScheduled.length === 0}>
                <LeftArrowSvg />
              </ArrowButton>
            </ArrowColumn>

            <ContentColumn>
              <ColumnHeader>편성 완료된 콘텐츠</ColumnHeader>
              <ContentList>
                {scheduledContents.map((content, index) => (
                  <ContentItem
                    key={content._id}
                    selected={selectedScheduled.includes(index)}
                    onClick={() =>
                      setSelectedScheduled(prev =>
                        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
                      )
                    }
                  >
                    <CheckboxContainer>
                      {selectedScheduled.includes(index) ? <CheckedSvg /> : <UncheckedSvg />}
                    </CheckboxContainer>
                    <ContentTitle>{content.contentData?.title || content.dataAnalysis?.title}</ContentTitle>
                  </ContentItem>
                ))}
              </ContentList>
            </ContentColumn>
          </ContentArea>
        </ContentSection>

        <Footer>
          <CancelButton Large Gray Fill onClick={onClose} style={{ minWidth: "90px", height: "40px" }}>
            <Sub2 color="gray700">취소</Sub2>
          </CancelButton>
          <Button Large Primary Fill onClick={handleConfirm} style={{ minWidth: "90px", height: "40px" }}>
            <Sub2 color="white">저장</Sub2>
          </Button>
        </Footer>
      </PopupContainer>
    </>
  );
};

export default MoleculeApiContentPopup;

// 디자인 동일 스타일들
const PopupContainer = styled.div`
  width: 720px;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 40px 24px 40px;
  background: white;
`;

const Heading = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${palette.gray900};
  margin: 0;
  line-height: 1.2;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -4px;

  &:hover { background: ${palette.gray100}; }
`;

const ContentSection = styled.div`
  padding: 24px 40px 40px 40px;
  flex: 1;
  overflow-y: auto;
`;

const ContentArea = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const ContentColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ColumnHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #323232;
  margin: 0;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
`;

const ContentList = styled.div`
  border: 1px solid #e0e4eb;
  border-radius: 8px;
  min-height: 300px;
  max-height: 300px;
  overflow-y: auto;
  background: white;
`;

const ContentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  background: ${props => (props.selected ? "#ECF2FE" : "white")};

  &:hover { background: #f8f9fa; }
  &:last-child { border-bottom: none; }
`;

const CheckboxContainer = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentTitle = styled.span`
  font-size: 16px;
  color: #323232;
  user-select: none;
  text-align: left;
  word-wrap: break-word;
  line-height: 1.4;
`;

const ArrowColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  padding-top: 60px;
`;

const ArrowButton = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid #e0e4eb;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => (props.disabled ? "#ccc" : "#666")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #256ef4;
    color: #256ef4;
  }

  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const Footer = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px 40px;
  background: white;
  justify-content: center;
`;

const CancelButton = styled(Button)`
  &:hover { background: ${palette.gray200} !important; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

// 체크박스/화살표 아이콘들
const UncheckedSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="18" height="18" rx="3" fill="transparent" stroke="rgba(179,183,186,0.5)" strokeWidth="1" />
  </svg>
);

const CheckedSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="18" height="18" rx="3" fill="#256EF4" />
    <path d="M8.5 12.5L6 10L5.3 10.7L8.5 13.9L14.7 7.7L14 7L8.5 12.5Z" fill="white" />
  </svg>
);

const RightArrowSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LeftArrowSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);


